import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Save, Send, Loader2, Plus, Trash2, Image, FileText,
  CheckCircle, Clock, AlertTriangle, Eye, Edit, Home, Bed, Bath,
  Sofa, Car, Trees, MapPin, Ruler, PoundSterling, MessageSquare,
  ThumbsUp, ThumbsDown, History
} from "lucide-react";
import { API } from "@/App";

const PROPERTY_TYPES = [
  "Detached House", "Semi-Detached House", "Terraced House", "End of Terrace",
  "Bungalow", "Flat/Apartment", "Maisonette", "Cottage", "Town House"
];

const TENURES = ["Freehold", "Leasehold", "Share of Freehold"];

const EPC_RATINGS = ["A", "B", "C", "D", "E", "F", "G"];

const COUNCIL_TAX_BANDS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const STATUS_INFO = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: Edit },
  pending_review: { label: "Sent for Review", color: "bg-amber-100 text-amber-700", icon: Clock },
  amendments_requested: { label: "Amendments Requested", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
  approved: { label: "Approved by Seller", color: "bg-green-100 text-green-700", icon: CheckCircle },
  completed: { label: "Live - For Sale", color: "bg-emerald-100 text-emerald-700", icon: Home },
};

export const SalesParticularsPage = () => {
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const isSellerReview = searchParams.get("review") === "true";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [particulars, setParticulars] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [floorplans, setFloorplans] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    property_type: "",
    bedrooms: "",
    bathrooms: "",
    reception_rooms: "",
    summary: "",
    full_description: "",
    location_description: "",
    features: [],
    rooms: [],
    garden: "",
    parking: "",
    tenure: "",
    // Leasehold fields
    lease_years_remaining: "",
    ground_rent: "",
    ground_rent_review_period: "",
    service_charge: "",
    service_charge_review_period: "",
    is_shared_ownership: false,
    shared_ownership_percentage: "",
    additional_tenure_info: "",
    floor_area_sqft: "",
    floor_area_sqm: "",
    // Standard fields
    council_tax_band: "",
    epc_rating: "",
    photo_ids: [],
    floorplan_ids: []
  });

  // Feature input
  const [newFeature, setNewFeature] = useState("");
  
  // Room input
  const [newRoom, setNewRoom] = useState({ name: "", dimensions: "", description: "" });
  
  // Send modal
  const [showSendModal, setShowSendModal] = useState(false);
  const [sellerEmail, setSellerEmail] = useState("");
  
  // Review modal (for seller)
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewComments, setReviewComments] = useState("");
  const [amendments, setAmendments] = useState([]);
  const [newAmendment, setNewAmendment] = useState({ field: "", suggestion: "" });

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [caseRes, particularsRes, photosRes] = await Promise.all([
        axios.get(`${API}/sales/cases/${caseId}`, { headers }),
        axios.get(`${API}/documents/particulars/case/${caseId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/documents/photos/case/${caseId}`, { headers })
      ]);

      setCaseData(caseRes.data.case);
      setPhotos(photosRes.data.photos || []);
      setFloorplans(photosRes.data.floorplans || []);

      if (particularsRes.data) {
        setParticulars(particularsRes.data);
        setFormData({
          property_type: particularsRes.data.property_type || "",
          bedrooms: particularsRes.data.bedrooms || "",
          bathrooms: particularsRes.data.bathrooms || "",
          reception_rooms: particularsRes.data.reception_rooms || "",
          summary: particularsRes.data.summary || "",
          full_description: particularsRes.data.full_description || "",
          location_description: particularsRes.data.location_description || "",
          features: particularsRes.data.features || [],
          rooms: particularsRes.data.rooms || [],
          garden: particularsRes.data.garden || "",
          parking: particularsRes.data.parking || "",
          tenure: particularsRes.data.tenure || "",
          // Leasehold fields
          lease_years_remaining: particularsRes.data.lease_years_remaining || "",
          ground_rent: particularsRes.data.ground_rent || "",
          ground_rent_review_period: particularsRes.data.ground_rent_review_period || "",
          service_charge: particularsRes.data.service_charge || "",
          service_charge_review_period: particularsRes.data.service_charge_review_period || "",
          is_shared_ownership: particularsRes.data.is_shared_ownership || false,
          shared_ownership_percentage: particularsRes.data.shared_ownership_percentage || "",
          additional_tenure_info: particularsRes.data.additional_tenure_info || "",
          floor_area_sqft: particularsRes.data.floor_area_sqft || "",
          floor_area_sqm: particularsRes.data.floor_area_sqm || "",
          // Standard fields
          council_tax_band: particularsRes.data.council_tax_band || "",
          epc_rating: particularsRes.data.epc_rating || "",
          photo_ids: particularsRes.data.photo_ids || [],
          floorplan_ids: particularsRes.data.floorplan_ids || []
        });
        
        // If there are seller amendments, show them
        if (particularsRes.data.seller_amendments?.length > 0) {
          setAmendments(particularsRes.data.seller_amendments);
        }
        if (particularsRes.data.seller_comments) {
          setReviewComments(particularsRes.data.seller_comments);
        }
      }

      // Get seller email
      const parties = caseRes.data.parties || [];
      const seller = parties.find(p => p.role === "seller");
      if (seller?.email) {
        setSellerEmail(seller.email);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      if (particulars) {
        // Update existing
        await axios.put(`${API}/documents/particulars/${particulars.particulars_id}`, formData, { headers });
        toast.success("Particulars saved");
      } else {
        // Create new
        const res = await axios.post(`${API}/documents/particulars/create`, {
          case_id: caseId,
          ...formData
        }, { headers });
        setParticulars(res.data);
        toast.success("Particulars created");
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSendForReview = async () => {
    if (!sellerEmail) {
      toast.error("Seller email required");
      return;
    }

    setSending(true);
    try {
      // Save first
      await handleSave();

      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/documents/particulars/${particulars.particulars_id}/send-for-review`, {
        seller_email: sellerEmail
      }, { headers });

      toast.success(`Particulars sent to ${sellerEmail} for review`);
      setShowSendModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleSellerReview = async (approved) => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/documents/particulars/${particulars.particulars_id}/seller-review`, {
        particulars_id: particulars.particulars_id,
        approved,
        comments: reviewComments,
        amendments: approved ? [] : amendments
      }, { headers });

      if (approved) {
        toast.success("Particulars approved! Property ready to list.");
      } else {
        toast.success("Amendment requests submitted");
      }
      
      setShowReviewModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSaving(false);
    }
  };

  const handleApproveAndList = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/documents/particulars/${particulars.particulars_id}/approve`, {}, { headers });

      toast.success("Property is now listed for sale!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const addRoom = () => {
    if (newRoom.name.trim()) {
      setFormData({ ...formData, rooms: [...formData.rooms, { ...newRoom }] });
      setNewRoom({ name: "", dimensions: "", description: "" });
    }
  };

  const removeRoom = (index) => {
    setFormData({
      ...formData,
      rooms: formData.rooms.filter((_, i) => i !== index)
    });
  };

  const togglePhoto = (photoId) => {
    const current = formData.photo_ids || [];
    if (current.includes(photoId)) {
      setFormData({ ...formData, photo_ids: current.filter(id => id !== photoId) });
    } else {
      setFormData({ ...formData, photo_ids: [...current, photoId] });
    }
  };

  const toggleFloorplan = (fpId) => {
    const current = formData.floorplan_ids || [];
    if (current.includes(fpId)) {
      setFormData({ ...formData, floorplan_ids: current.filter(id => id !== fpId) });
    } else {
      setFormData({ ...formData, floorplan_ids: [...current, fpId] });
    }
  };

  const addAmendment = () => {
    if (newAmendment.field && newAmendment.suggestion) {
      setAmendments([...amendments, { ...newAmendment }]);
      setNewAmendment({ field: "", suggestion: "" });
    }
  };

  const removeAmendment = (index) => {
    setAmendments(amendments.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusInfo = STATUS_INFO[particulars?.status] || STATUS_INFO.draft;
  const StatusIcon = statusInfo.icon;
  const canEdit = !particulars || particulars.status === "draft" || particulars.status === "amendments_requested";
  const canSendForReview = particulars && (particulars.status === "draft" || particulars.status === "amendments_requested");
  const canApprove = particulars?.status === "approved";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/sales/case/${caseId}/documents`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Sales Particulars</h1>
                <p className="text-sm text-muted-foreground">
                  {caseData?.address_line1}, {caseData?.postcode}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={statusInfo.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusInfo.label}
              </Badge>
              
              {canEdit && (
                <Button variant="outline" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Draft
                </Button>
              )}
              
              {canSendForReview && (
                <Button onClick={() => setShowSendModal(true)}>
                  <Send className="w-4 h-4 mr-2" />
                  Send for Review
                </Button>
              )}
              
              {canApprove && (
                <Button onClick={handleApproveAndList} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve & List Property
                </Button>
              )}

              {isSellerReview && particulars?.status === "pending_review" && (
                <Button onClick={() => setShowReviewModal(true)}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Submit Review
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Amendments Alert */}
        {particulars?.status === "amendments_requested" && amendments.length > 0 && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-5 h-5" />
                Seller Requested Amendments
              </CardTitle>
              {reviewComments && (
                <CardDescription className="text-orange-600">
                  "{reviewComments}"
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {amendments.map((am, i) => (
                  <div key={i} className="p-3 bg-white rounded-lg border border-orange-200">
                    <p className="font-medium text-sm">{am.field}</p>
                    <p className="text-sm text-muted-foreground">{am.suggestion}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  Property Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Property Type</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(v) => setFormData({ ...formData, property_type: v })}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Asking Price</Label>
                    <div className="flex items-center gap-2">
                      <PoundSterling className="w-4 h-4 text-muted-foreground" />
                      <Input
                        value={caseData?.asking_price?.toLocaleString() || ""}
                        disabled
                        className="bg-slate-50"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <Bed className="w-4 h-4" /> Bedrooms
                    </Label>
                    <Input
                      type="number"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Bath className="w-4 h-4" /> Bathrooms
                    </Label>
                    <Input
                      type="number"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Sofa className="w-4 h-4" /> Receptions
                    </Label>
                    <Input
                      type="number"
                      value={formData.reception_rooms}
                      onChange={(e) => setFormData({ ...formData, reception_rooms: e.target.value })}
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Tenure</Label>
                    <Select
                      value={formData.tenure}
                      onValueChange={(v) => setFormData({ ...formData, tenure: v })}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {TENURES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Council Tax Band</Label>
                    <Select
                      value={formData.council_tax_band}
                      onValueChange={(v) => setFormData({ ...formData, council_tax_band: v })}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNCIL_TAX_BANDS.map(b => (
                          <SelectItem key={b} value={b}>Band {b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>EPC Rating</Label>
                    <Select
                      value={formData.epc_rating}
                      onValueChange={(v) => setFormData({ ...formData, epc_rating: v })}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {EPC_RATINGS.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Leasehold Fields - Show only when tenure is Leasehold */}
                {(formData.tenure === "Leasehold" || formData.tenure === "Share of Freehold") && (
                  <div className="border-t pt-4 mt-4 space-y-4">
                    <h4 className="font-medium text-slate-700 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Leasehold Information
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Years Remaining on Lease</Label>
                        <Input
                          type="number"
                          value={formData.lease_years_remaining}
                          onChange={(e) => setFormData({ ...formData, lease_years_remaining: e.target.value })}
                          placeholder="e.g., 85"
                          disabled={!canEdit}
                        />
                      </div>
                      <div>
                        <Label>Ground Rent (£/year)</Label>
                        <Input
                          type="number"
                          value={formData.ground_rent}
                          onChange={(e) => setFormData({ ...formData, ground_rent: e.target.value })}
                          placeholder="e.g., 150"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Ground Rent Review Period</Label>
                        <Input
                          value={formData.ground_rent_review_period}
                          onChange={(e) => setFormData({ ...formData, ground_rent_review_period: e.target.value })}
                          placeholder="e.g., Every 10 years"
                          disabled={!canEdit}
                        />
                      </div>
                      <div>
                        <Label>Service Charge (£/year)</Label>
                        <Input
                          type="number"
                          value={formData.service_charge}
                          onChange={(e) => setFormData({ ...formData, service_charge: e.target.value })}
                          placeholder="e.g., 1200"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Service Charge Review Period</Label>
                        <Input
                          value={formData.service_charge_review_period}
                          onChange={(e) => setFormData({ ...formData, service_charge_review_period: e.target.value })}
                          placeholder="e.g., Annually"
                          disabled={!canEdit}
                        />
                      </div>
                      <div className="flex items-center gap-4 pt-6">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="shared_ownership"
                            checked={formData.is_shared_ownership}
                            onCheckedChange={(checked) => setFormData({ ...formData, is_shared_ownership: checked })}
                            disabled={!canEdit}
                          />
                          <Label htmlFor="shared_ownership" className="cursor-pointer">Shared Ownership</Label>
                        </div>
                        {formData.is_shared_ownership && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              className="w-20"
                              value={formData.shared_ownership_percentage}
                              onChange={(e) => setFormData({ ...formData, shared_ownership_percentage: e.target.value })}
                              placeholder="%"
                              disabled={!canEdit}
                            />
                            <span className="text-sm text-slate-500">% share</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Additional Tenure Information</Label>
                      <Textarea
                        value={formData.additional_tenure_info}
                        onChange={(e) => setFormData({ ...formData, additional_tenure_info: e.target.value })}
                        placeholder="Any additional tenure details, restrictions, or notes..."
                        rows={2}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                )}

                {/* Floor Area */}
                <div className="grid grid-cols-2 gap-4 border-t pt-4 mt-4">
                  <div>
                    <Label>Floor Area (sq ft)</Label>
                    <Input
                      type="number"
                      value={formData.floor_area_sqft}
                      onChange={(e) => {
                        const sqft = e.target.value;
                        const sqm = sqft ? Math.round(parseFloat(sqft) * 0.092903) : "";
                        setFormData({ ...formData, floor_area_sqft: sqft, floor_area_sqm: sqm });
                      }}
                      placeholder="e.g., 1200"
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label>Floor Area (sq m)</Label>
                    <Input
                      type="number"
                      value={formData.floor_area_sqm}
                      onChange={(e) => {
                        const sqm = e.target.value;
                        const sqft = sqm ? Math.round(parseFloat(sqm) / 0.092903) : "";
                        setFormData({ ...formData, floor_area_sqm: sqm, floor_area_sqft: sqft });
                      }}
                      placeholder="e.g., 111"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Descriptions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Descriptions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Summary (shown in search results)</Label>
                  <Textarea
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    placeholder="Brief property summary..."
                    rows={2}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label>Full Description</Label>
                  <Textarea
                    value={formData.full_description}
                    onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
                    placeholder="Detailed property description..."
                    rows={6}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Location Description
                  </Label>
                  <Textarea
                    value={formData.location_description}
                    onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                    placeholder="Describe the area, nearby amenities, transport links..."
                    rows={3}
                    disabled={!canEdit}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card>
              <CardHeader>
                <CardTitle>Key Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  {formData.features.map((feature, i) => (
                    <Badge key={i} variant="secondary" className="px-3 py-1">
                      {feature}
                      {canEdit && (
                        <button onClick={() => removeFeature(i)} className="ml-2 hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Add a feature..."
                      onKeyDown={(e) => e.key === "Enter" && addFeature()}
                    />
                    <Button variant="outline" onClick={addFeature}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rooms */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ruler className="w-5 h-5" />
                  Room Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  {formData.rooms.map((room, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg flex justify-between items-start">
                      <div>
                        <p className="font-medium">{room.name}</p>
                        {room.dimensions && (
                          <p className="text-sm text-muted-foreground">{room.dimensions}</p>
                        )}
                        {room.description && (
                          <p className="text-sm mt-1">{room.description}</p>
                        )}
                      </div>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => removeRoom(i)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <div className="space-y-2 p-3 border rounded-lg">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={newRoom.name}
                        onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                        placeholder="Room name (e.g., Living Room)"
                      />
                      <Input
                        value={newRoom.dimensions}
                        onChange={(e) => setNewRoom({ ...newRoom, dimensions: e.target.value })}
                        placeholder="Dimensions (e.g., 5.2m x 4.1m)"
                      />
                    </div>
                    <Input
                      value={newRoom.description}
                      onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                      placeholder="Description (optional)"
                    />
                    <Button variant="outline" onClick={addRoom} className="w-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Room
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* External */}
            <Card>
              <CardHeader>
                <CardTitle>External Features</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Trees className="w-4 h-4" /> Garden
                  </Label>
                  <Textarea
                    value={formData.garden}
                    onChange={(e) => setFormData({ ...formData, garden: e.target.value })}
                    placeholder="Describe the garden..."
                    rows={2}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Car className="w-4 h-4" /> Parking
                  </Label>
                  <Textarea
                    value={formData.parking}
                    onChange={(e) => setFormData({ ...formData, parking: e.target.value })}
                    placeholder="Describe parking arrangements..."
                    rows={2}
                    disabled={!canEdit}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Photos Selection */}
          <div className="space-y-6">
            {/* Photos */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Photos ({formData.photo_ids?.length || 0} selected)
                </CardTitle>
                <CardDescription>
                  Select photos to include in particulars
                </CardDescription>
              </CardHeader>
              <CardContent>
                {photos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {photos.map((photo) => (
                      <div
                        key={photo.photo_id}
                        onClick={() => canEdit && togglePhoto(photo.photo_id)}
                        className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          formData.photo_ids?.includes(photo.photo_id)
                            ? "border-blue-500 ring-2 ring-blue-200"
                            : "border-transparent hover:border-slate-300"
                        }`}
                      >
                        <img
                          src={`${API}${photo.file_url}`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        {formData.photo_ids?.includes(photo.photo_id) && (
                          <div className="absolute top-1 right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No photos uploaded yet.{" "}
                    <button
                      onClick={() => navigate(`/sales/case/${caseId}/documents`)}
                      className="text-blue-600 hover:underline"
                    >
                      Upload photos
                    </button>
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Floorplans */}
            <Card>
              <CardHeader>
                <CardTitle>Floorplans ({formData.floorplan_ids?.length || 0} selected)</CardTitle>
              </CardHeader>
              <CardContent>
                {floorplans.length > 0 ? (
                  <div className="space-y-2">
                    {floorplans.map((fp) => (
                      <div
                        key={fp.photo_id}
                        onClick={() => canEdit && toggleFloorplan(fp.photo_id)}
                        className={`p-2 rounded-lg border cursor-pointer transition-all ${
                          formData.floorplan_ids?.includes(fp.photo_id)
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={formData.floorplan_ids?.includes(fp.photo_id)} />
                          <span className="text-sm">{fp.filename}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No floorplans uploaded yet.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Quick Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm space-y-2">
                  <p className="font-semibold text-lg">
                    £{caseData?.asking_price?.toLocaleString()}
                  </p>
                  <p className="font-medium">{caseData?.address_line1}</p>
                  <p className="text-muted-foreground">{caseData?.postcode}</p>
                  <div className="flex gap-3 text-muted-foreground">
                    {formData.bedrooms && <span>{formData.bedrooms} bed</span>}
                    {formData.bathrooms && <span>{formData.bathrooms} bath</span>}
                    {formData.reception_rooms && <span>{formData.reception_rooms} reception</span>}
                  </div>
                  {formData.summary && (
                    <p className="text-sm mt-2">{formData.summary}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Send for Review Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send for Seller Review</DialogTitle>
            <DialogDescription>
              The seller will receive an email with a link to review and approve the particulars.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Seller Email</Label>
              <Input
                type="email"
                value={sellerEmail}
                onChange={(e) => setSellerEmail(e.target.value)}
                placeholder="seller@email.com"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendForReview} disabled={sending}>
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seller Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Sales Particulars</DialogTitle>
            <DialogDescription>
              Review the property details and either approve or request changes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Comments (optional)</Label>
              <Textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                placeholder="Any general comments..."
                rows={2}
              />
            </div>

            <div className="border-t pt-4">
              <Label className="mb-2 block">Request Changes</Label>
              <div className="space-y-2 mb-3">
                {amendments.map((am, i) => (
                  <div key={i} className="p-2 bg-slate-50 rounded flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{am.field}</p>
                      <p className="text-sm text-muted-foreground">{am.suggestion}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeAmendment(i)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="space-y-2 p-3 border rounded">
                <Input
                  value={newAmendment.field}
                  onChange={(e) => setNewAmendment({ ...newAmendment, field: e.target.value })}
                  placeholder="Field to change (e.g., Description)"
                />
                <Input
                  value={newAmendment.suggestion}
                  onChange={(e) => setNewAmendment({ ...newAmendment, suggestion: e.target.value })}
                  placeholder="Suggested change"
                />
                <Button variant="outline" onClick={addAmendment} size="sm" className="w-full">
                  <Plus className="w-3 h-3 mr-1" />
                  Add Amendment
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => handleSellerReview(false)}
              disabled={saving || amendments.length === 0}
              className="flex-1"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Request Changes
            </Button>
            <Button
              onClick={() => handleSellerReview(true)}
              disabled={saving}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ThumbsUp className="w-4 h-4 mr-2" />
              )}
              Approve Particulars
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesParticularsPage;
